import { supabase } from '../lib/supabase';

export class QRDynamicRedirectService {
  // Get current redirect URL for a QR code
  static async getCurrentRedirectUrl(qrCodeId) {
    try {
      const { data, error } = await supabase?.rpc('get_current_redirect_url', {
        qr_code_uuid: qrCodeId
      });

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Get all redirects for a QR code (history)
  static async getQRRedirectHistory(qrCodeId) {
    try {
      const { data, error } = await supabase
        ?.from('qr_dynamic_redirects')
        ?.select(`
          id,
          destination_url,
          is_active,
          created_at,
          updated_at,
          created_by
        `)
        ?.eq('qr_code_id', qrCodeId)
        ?.order('created_at', { ascending: false });

      if (error) {
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  }

  // Add new dynamic redirect (keeps QR code the same, changes destination)
  static async addDynamicRedirect(qrCodeId, destinationUrl, userId) {
    try {
      // Deactivate previous redirects for this QR code
      await supabase
        ?.from('qr_dynamic_redirects')
        ?.update({ is_active: false })
        ?.eq('qr_code_id', qrCodeId);

      // Add new active redirect
      const { data, error } = await supabase
        ?.from('qr_dynamic_redirects')
        ?.insert({
          qr_code_id: qrCodeId,
          destination_url: destinationUrl,
          is_active: true,
          created_by: userId
        })
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Update existing redirect
  static async updateDynamicRedirect(redirectId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('qr_dynamic_redirects')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', redirectId)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Activate/deactivate a redirect
  static async toggleRedirect(redirectId, isActive) {
    try {
      const { data, error } = await supabase
        ?.from('qr_dynamic_redirects')
        ?.update({ 
          is_active: isActive,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', redirectId)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Delete a redirect
  static async deleteRedirect(redirectId) {
    try {
      const { error } = await supabase
        ?.from('qr_dynamic_redirects')
        ?.delete()
        ?.eq('id', redirectId);

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  }

  // Get QR codes with their current redirect URLs
  static async getQRCodesWithRedirects(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('qr_codes')
        ?.select(`
          id,
          name,
          content,
          qr_type,
          short_url,
          is_active,
          created_at,
          updated_at,
          qr_dynamic_redirects!inner (
            id,
            destination_url,
            is_active,
            created_at
          )
        `)
        ?.eq('owner_id', userId)
        ?.eq('qr_dynamic_redirects.is_active', true)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        return { data: [], error: error?.message };
      }

      // Transform data to include current redirect info
      const transformedData = data?.map(qr => ({
        ...qr,
        currentRedirect: qr?.qr_dynamic_redirects?.[0] || null,
        hasRedirect: qr?.qr_dynamic_redirects?.length > 0
      }));

      return { data: transformedData || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  }
}